import * as React from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { useState } from "react";
import { suppliersSortedSelector } from "../../reducers/suppliers";
import { Supplier } from "../../interfaces/StockInterfaces";
import hasOwnProperty from "../../helpers/object";
import { t, tl } from "../../components/translate";
import Panel from "../../components/Panel";
import SupplierCreationForm from "./CreateSupplierForm/SupplierForm";
import { supplierActions } from "../../actions";
import * as NotificationActions from "../../actions/notification";
import { removeGeneralErrorByKey } from "../../actions/error";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { createErrorMessage, MODULE } from "../../helpers/messages";

interface Props {
  onChange: (s: Supplier, b?: boolean) => void;
  error?: boolean;
}

export const SuppliersAutoSelect = ({ onChange, error = false }: Props): React.JSX.Element => {
  const suppliers = useAppSelector((state) =>
    suppliersSortedSelector(state).filter((item) => Boolean(item.active))
  );
  const [newSupplier, setNewSupplier] = useState({ createMode: false, name: "" });
  const dispatch = useAppDispatch();

  return (
    <>
      <Autocomplete
        options={suppliers as Array<Supplier | { inputValue: string; name: string }>}
        clearOnBlur
        style={{ width: 300 }}
        freeSolo
        onChange={(_, v) => {
          if (typeof v === "string") {
            return;
          }
          if (hasOwnProperty(v, "inputValue") && v?.inputValue) {
            setNewSupplier({ createMode: true, name: v.inputValue });
          } else {
            onChange(v as Supplier);
          }
        }}
        filterOptions={(options, params) => {
          if (params.inputValue !== "") {
            options.push({
              inputValue: params.inputValue,
              name: `Add "${params.inputValue}"`
            });
          }
          return options.filter((opt) => {
            const filterString = (hasOwnProperty(opt, "name") ? opt.name : "").toLowerCase();
            return (
              filterString.includes(params.inputValue.toLowerCase()) || filterString.includes("add")
            );
          });
        }}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            data-testmation="selectSupplier"
            autoFocus
            error={error}
            variant="outlined"
            margin="dense"
            placeholder={t("stock.selectSupplier")}
          />
        )}
        renderOption={(ownProps, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...ownProps} key={"id" in option ? option.id : "AddSupplierKey"}>
            {option.name}
          </li>
        )}
        getOptionLabel={(option) => option.name}
      />
      {newSupplier.createMode && (
        <Panel
          title={t("employee.createSupplier")}
          onClose={() => {
            setNewSupplier({ createMode: false, name: "" });
          }}
        >
          <Box width="100%">
            <SupplierCreationForm
              data={{ name: newSupplier.name }}
              createMode
              onCancel={() => {
                setNewSupplier({ createMode: false, name: "" });
              }}
              onSave={async (data) => {
                try {
                  await dispatch(
                    supplierActions.postSupplier(data, (_supplier) => {
                      onChange(_supplier, true);
                    })
                  );
                  dispatch(
                    NotificationActions.notificationAdd({
                      id: new Date().getUTCMilliseconds(),
                      variant: "success",
                      message: tl("successSupplier"),
                      autoTimeout: true
                    })
                  );
                } catch (e) {
                  dispatch(
                    NotificationActions.notificationAdd({
                      id: new Date().getUTCMilliseconds(),
                      variant: "error",
                      message: e.message || createErrorMessage(MODULE.SUPPLIER),
                      autoTimeout: true
                    })
                  );
                  dispatch(removeGeneralErrorByKey("POST_SUPPLIER"));
                } finally {
                  setNewSupplier({ createMode: false, name: "" });
                }
              }}
            />
          </Box>
        </Panel>
      )}
    </>
  );
};

export default SuppliersAutoSelect;
